'use client';

import React, { useState } from 'react';
import { InteractiveRevealComponent } from '../types/content-components.types.js';
import { ComponentIcon } from './ComponentIcon.js';
import { Eye, EyeOff, HelpCircle } from 'lucide-react';

interface InteractiveRevealProps {
  component: InteractiveRevealComponent;
}

export const InteractiveReveal: React.FC<InteractiveRevealProps> = ({ component }) => {
  const { props } = component;
  const [isRevealed, setIsRevealed] = useState(false);
  const [showHint, setShowHint] = useState(false);

  const getPromptIcon = () => {
    switch (props.promptType) {
      case 'question':
        return <HelpCircle className="w-6 h-6 text-indigo-600" />;
      case 'challenge':
        return <ComponentIcon icon="target" className="w-6 h-6 text-purple-600" />;
      case 'exercise':
        return <ComponentIcon icon="brain" className="w-6 h-6 text-emerald-600" />;
      default:
        return props.icon && <ComponentIcon icon={props.icon} className="w-6 h-6" />;
    }
  };

  const getPromptColor = () => {
    switch (props.promptType) {
      case 'question':
        // Muted sky blue for questions
        return 'border-learn-sky-200 bg-learn-sky-50';
      case 'challenge':
        // Muted honey for challenges
        return 'border-learn-honey-200 bg-learn-honey-50';
      case 'exercise':
        // Growth green for exercises (muted)
        return 'border-green-200 bg-green-50';
      default:
        // Muted sage for default
        return 'border-learn-sage-200 bg-learn-sage-50';
    }
  };

  return (
    <div className="w-full space-y-3">
      {/* Prompt Section */}
      <div className={`border-2 ${getPromptColor()} rounded-xl p-5 transition-all duration-300`}>
        <div className="flex items-start gap-3 mb-4">
          {getPromptIcon()}
          <div className="flex-1">
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
              {props.promptType || 'Think About It'}
            </div>
            <div className="text-base font-medium leading-relaxed"
                 style={{ color: '#111827' }}
                 dangerouslySetInnerHTML={{ 
                   __html: props.prompt
                     .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600; color: #111827;">$1</strong>')
                     .replace(/\*(.*?)\*/g, '<em style="font-style: italic; color: #111827;">$1</em>')
                     .replace(/\n/g, '<br />')
                 }}
            />
          </div>
        </div>

        {/* Hint Section (if available and not revealed yet) */}
        {props.hint && !isRevealed && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <button
              onClick={() => setShowHint(!showHint)}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2 transition-colors"
            >
              <ComponentIcon icon="lightbulb" className="w-4 h-4" />
              {showHint ? 'Hide Hint' : 'Show Hint'}
            </button>
            {showHint && (
              <p className="text-sm text-gray-700 mt-2 ml-6 italic">
                💡 {props.hint}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Reveal Button */}
      {!isRevealed && (
        <button
          onClick={() => setIsRevealed(true)}
          className="w-full px-4 py-3 bg-gradient-to-r from-learn-coral-400 to-learn-clay-400 text-white rounded-lg font-medium hover:from-learn-coral-500 hover:to-learn-clay-500 transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
        >
          <Eye className="w-4 h-4" />
          {props.revealLabel || 'Reveal Answer'}
        </button>
      )}

      {/* Revealed Content */}
      {isRevealed && (
        <div className="bg-white border-2 border-green-400 rounded-xl p-5 shadow-md animate-slideDown">
          <div className="flex items-start gap-3 mb-3">
            <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="flex-1">
              <div className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-2">
                Answer
              </div>
              <div className="text-base leading-relaxed"
                   style={{ color: '#111827' }}
                   dangerouslySetInnerHTML={{ 
                     __html: props.revealContent
                       .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600; color: #111827;">$1</strong>')
                       .replace(/\*(.*?)\*/g, '<em style="font-style: italic; color: #111827;">$1</em>')
                       .replace(/\n/g, '<br />')
                   }}
              />
            </div>
          </div>

          <button
            onClick={() => {
              setIsRevealed(false);
              setShowHint(false);
            }}
            className="mt-3 text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2"
          >
            <EyeOff className="w-4 h-4" />
            Hide Answer
          </button>
        </div>
      )}
    </div>
  );
};

